import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import * as prdApi from '~/services/api/prd';
import { logger } from '~/utils/logger';

import type { ActiveSessionStatus, StartSessionRequest, StartSessionResponse } from '~/types';

/**
 * Return type for usePRDGeneration hook.
 */
export interface UsePRDGenerationReturn {
  // Mutations
  startSession: UseMutationResult<StartSessionResponse, Error, StartSessionRequest>;
  cancelSession: UseMutationResult<void, Error, string>;

  // Helper states
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook for checking active PRD session status on a project.
 *
 * Use this hook to poll for active session status on the project list.
 * Enables the spinning icon indicator when someone is generating a PRD.
 *
 * @example
 * ```tsx
 * const { data: status } = useActiveSessionStatus(project.id);
 * if (status?.is_generating) {
 *   // Show spinning icon
 * }
 * ```
 *
 * @param projectId - Project ID to check (null to disable query)
 * @returns Query result for active session status
 */
export function useActiveSessionStatus(
  projectId: string | null,
): UseQueryResult<ActiveSessionStatus, Error> {
  return useQuery<ActiveSessionStatus, Error>({
    queryKey: ['prd-active-status', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      return await prdApi.checkActiveSession(projectId);
    },
    enabled: !!projectId && !projectId.startsWith('temp-'), // Skip temp projects (not yet saved)
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Poll every 30 seconds
    retry: 1, // Only retry once
    retryDelay: 1000,
  });
}

/**
 * Custom hook for PRD generation operations.
 *
 * Wraps API calls with TanStack Query for caching and loading states.
 *
 * MVP Features (Simplified):
 * - TanStack Query caching (5 min stale time for preview)
 * - Loading states with progress indicators
 * - Error handling with manual retry
 * - Network requirement (no offline queue)
 *
 * Future Features (Deferred):
 * - Offline queue (localStorage)
 * - Optimistic updates
 * - Auto-retry with exponential backoff
 *
 * @param _projectId - Project UUID for session context (currently unused but kept for future use)
 * @returns Hook interface with mutations, queries, and helper states
 */
export function usePRDGeneration(_projectId: string): UsePRDGenerationReturn {
  const queryClient = useQueryClient();

  // Mutation: Start Session
  const startSession = useMutation<StartSessionResponse, Error, StartSessionRequest>({
    mutationKey: ['prd', 'start-session'],
    mutationFn: async request => {
      return await prdApi.startSession(request);
    },
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) - these are validation errors that won't succeed on retry
      // Only retry on network errors or server errors (5xx)
      // Check if error has status property (ApiError from apiRequest)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false; // Don't retry 4xx errors
        }
      }
      return failureCount < 3;
    },
    onSuccess: data => {
      // Cache session data
      queryClient.setQueryData(['prd-session', data.session_id], data);

      // Invalidate batch query to immediately update project card status
      void queryClient.invalidateQueries({ queryKey: ['prd-session-status-batch'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: error => {
      logger.error(
        { component: 'usePRDGeneration' },
        'Failed to start PRD session',
        error instanceof Error ? error : new Error(String(error)),
      );
      // Error is automatically available in mutation.error
    },
  });

  // Mutation: Cancel Session
  const cancelSession = useMutation<void, Error, string>({
    mutationKey: ['prd', 'cancel-session'],
    mutationFn: async sessionId => {
      return await prdApi.cancelSession(sessionId);
    },
    onSuccess: (_, sessionId) => {
      // Clear session cache
      queryClient.removeQueries({ queryKey: ['prd-session', sessionId] });
      queryClient.removeQueries({ queryKey: ['prd-preview', sessionId] });

      // Invalidate active session status queries
      void queryClient.invalidateQueries({ queryKey: ['prd-active-status'] });

      // Invalidate batch query to immediately update project card status
      void queryClient.invalidateQueries({ queryKey: ['prd-session-status-batch'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });

      logger.info({ component: 'usePRDGeneration' }, 'PRD session cancelled', { sessionId });
    },
    onError: error => {
      logger.error(
        { component: 'usePRDGeneration' },
        'Failed to cancel PRD session',
        error instanceof Error ? error : new Error(String(error)),
      );
    },
  });

  // Aggregate loading state
  const isLoading = startSession.isPending || cancelSession.isPending;

  // Aggregate error state (most recent error)
  const error = cancelSession.error || startSession.error || null;

  return {
    startSession,
    cancelSession,
    isLoading,
    error,
  };
}
